import { useFilterProcessingState } from './FilterProcessingStateContext';
import { UseHydrateOmicsExplorer } from './useHydrateOmicsExplorer';
import { useSelectedFilter } from './SelectedFilterContext';
import { useSelectedFilterOptions } from '../../../src/components/common/Genomics/SelectedFilterOptionsContext';
import {
  DefaultFilters,
  SequenceType,
  ActiveVariantType,
  SavedFilter,
} from './types';
import * as React from 'react';
import queryString from 'query-string';
import uniqBy from 'lodash/uniqBy';

interface UseCurrentDefaultFilter {
  defaultFilters?: DefaultFilters;
  selectedSequenceType?: SequenceType;
  selectedActiveVariantType?: ActiveVariantType;
  hydrateState?: UseHydrateOmicsExplorer['state'];
}

/**
 * @description A hook for determining the current default filter, based on
 * the provided default filters, the active variant type, and the active
 * sequence type.  It uses the variant and sequence type combination to
 * do a lookup on the defaultFilters object to see if we have a current
 * filter or not.
 *
 * The hydrateState prop is used to ensure things settle before we do the
 * lookup.
 */
export function useCurrentDefaultFilter({
  defaultFilters,
  selectedActiveVariantType,
  selectedSequenceType,
  hydrateState,
}: UseCurrentDefaultFilter) {
  const [, setSelectedFilterOptions] = useSelectedFilterOptions();
  const [, setSelectedFilter] = useSelectedFilter();
  const [, setFilterProcessingState] = useFilterProcessingState();

  const [currentDefaultFilter, setCurrentDefaultFilter] =
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    React.useState<SavedFilter>(null);

  // determine which is the current default filter
  React.useEffect(() => {
    if (!defaultFilters) {
      return;
    }

    const defaultFilterBasedOnSelections =
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      defaultFilters[
        `${selectedSequenceType}${
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          selectedActiveVariantType.charAt(0).toUpperCase() +
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          selectedActiveVariantType.slice(1)
        }`
      ];

    if (!defaultFilterBasedOnSelections) {
      return;
    }

    setCurrentDefaultFilter(defaultFilterBasedOnSelections);
  }, [defaultFilters, selectedActiveVariantType, selectedSequenceType]);

  // If we have a current default filter,
  // we can set the selected filter to it.
  React.useEffect(() => {
    if (hydrateState !== 'COMPLETE') {
      return;
    }

    if (!defaultFilters) {
      return;
    }

    setFilterProcessingState('INITIALIZING');

    const defaultFilterBasedOnSelections =
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      defaultFilters[
        `${selectedSequenceType}${
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          selectedActiveVariantType.charAt(0).toUpperCase() +
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          selectedActiveVariantType.slice(1)
        }`
      ];

    const { filters: filtersFromQueryString } = queryString.parse(
      window.location.hash,
    );

    // We do not want to call `setSelectedFilterOptions` as they
    // come from our query param
    if (
      defaultFilterBasedOnSelections?.id === 'default' &&
      filtersFromQueryString
    ) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setSelectedFilter(null);
      setFilterProcessingState('COMPLETE');
      return;
    }

    // We don't have query params, so we are safe resetting the
    // filter options
    if (
      defaultFilterBasedOnSelections?.id === 'default' &&
      !filtersFromQueryString
    ) {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      setSelectedFilter(null);
      setSelectedFilterOptions([]);
      setFilterProcessingState('COMPLETE');
      return;
    }

    setSelectedFilter(defaultFilterBasedOnSelections);
    setSelectedFilterOptions(
      uniqBy(
        [
          ...(defaultFilterBasedOnSelections?.params?.filters || []),
          ...(filtersFromQueryString
            ? JSON.parse(decodeURIComponent(filtersFromQueryString as string))
            : []),
        ],
        'id',
      ),
    );
    setFilterProcessingState('COMPLETE');
  }, [
    defaultFilters,
    hydrateState,
    selectedActiveVariantType,
    selectedSequenceType,
    setFilterProcessingState,
    setSelectedFilter,
    setSelectedFilterOptions,
  ]);

  const handleDefaultFilterUpdate = (newDefaultFilter: SavedFilter) => {
    setCurrentDefaultFilter(newDefaultFilter);
  };

  return {
    currentDefaultFilter,
    updateDefaultFilter: handleDefaultFilterUpdate,
  };
}
